import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DESTINATIONS_QUERY_PARAM = 'query';
export const DESTINATIONS_PAGE_PARAM = 'page';
export const DESTINATIONS_SORT_COLUMN_PARAM = 'sortColumn';
export const DESTINATIONS_SORT_ORDER_PARAM = 'sortOrder';
export const DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM = 'onlyWithoutPhotos';
export const DESTINATIONS_HAS_GUIDE_PARAM = 'hasGuide';
export const DESTINATIONS_SOURCE_PARAM = 'source';
export const DESTINATIONS_STATUS_PARAM = 'status';

export interface DestinationsListState {
    query: string;
    page: number;
    sortColumn?: string;
    sortOrder?: string;
    onlyWithoutPhotos?: boolean;
    hasGuide?: boolean;
    source?: number;
    status?: number;
    returnToUrl?: string;
}

export function useDestinationsListState() {
    const [searchParams, setSearchParams] = useSearchParams();

    const query = searchParams.get(DESTINATIONS_QUERY_PARAM) || '';
    const page = parseInt(searchParams.get(DESTINATIONS_PAGE_PARAM) || '1', 10);
    const sortColumn =
        searchParams.get(DESTINATIONS_SORT_COLUMN_PARAM) || undefined;
    const sortOrder =
        searchParams.get(DESTINATIONS_SORT_ORDER_PARAM) || undefined;
    const onlyWithoutPhotos =
        searchParams.get(DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM) === 'true';
    const hasGuide = searchParams.get(DESTINATIONS_HAS_GUIDE_PARAM) === 'true';
    const source = searchParams.get(DESTINATIONS_SOURCE_PARAM)
        ? parseInt(searchParams.get(DESTINATIONS_SOURCE_PARAM) || '0', 10)
        : undefined;
    const status = searchParams.get(DESTINATIONS_STATUS_PARAM)
        ? parseInt(searchParams.get(DESTINATIONS_STATUS_PARAM) || '0', 10)
        : undefined;

    const updateState = useCallback(
        (updates: Partial<DestinationsListState>) => {
            const newParams = new URLSearchParams(searchParams);

            if (updates.query !== undefined) {
                if (updates.query) {
                    newParams.set(DESTINATIONS_QUERY_PARAM, updates.query);
                } else {
                    newParams.delete(DESTINATIONS_QUERY_PARAM);
                }
            }

            if (updates.page !== undefined) {
                if (updates.page > 1) {
                    newParams.set(
                        DESTINATIONS_PAGE_PARAM,
                        updates.page.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_PAGE_PARAM);
                }
            }

            if (updates.sortColumn !== undefined) {
                if (updates.sortColumn) {
                    newParams.set(
                        DESTINATIONS_SORT_COLUMN_PARAM,
                        updates.sortColumn,
                    );
                } else {
                    newParams.delete(DESTINATIONS_SORT_COLUMN_PARAM);
                }
            }

            if (updates.sortOrder !== undefined) {
                if (updates.sortOrder) {
                    newParams.set(
                        DESTINATIONS_SORT_ORDER_PARAM,
                        updates.sortOrder,
                    );
                } else {
                    newParams.delete(DESTINATIONS_SORT_ORDER_PARAM);
                }
            }

            if (updates.onlyWithoutPhotos !== undefined) {
                if (updates.onlyWithoutPhotos) {
                    newParams.set(
                        DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM,
                        'true',
                    );
                } else {
                    newParams.delete(DESTINATIONS_ONLY_WITHOUT_PHOTOS_PARAM);
                }
            }

            if (updates.hasGuide !== undefined) {
                if (updates.hasGuide) {
                    newParams.set(DESTINATIONS_HAS_GUIDE_PARAM, 'true');
                } else {
                    newParams.delete(DESTINATIONS_HAS_GUIDE_PARAM);
                }
            }

            if (updates.source !== undefined) {
                if (typeof updates.source === 'number') {
                    newParams.set(
                        DESTINATIONS_SOURCE_PARAM,
                        updates.source.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_SOURCE_PARAM);
                }
            }

            if (updates.status !== undefined) {
                if (typeof updates.status === 'number') {
                    newParams.set(
                        DESTINATIONS_STATUS_PARAM,
                        updates.status.toString(),
                    );
                } else {
                    newParams.delete(DESTINATIONS_STATUS_PARAM);
                }
            }

            setSearchParams(newParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    return {
        query,
        page,
        sortColumn,
        sortOrder,
        onlyWithoutPhotos,
        hasGuide,
        source,
        status,
        returnToUrl: `/destinations/overview?${searchParams.toString()}`,
        updateState,
    };
}
